import { useState } from 'react';
import { ISnackbarProps, SnackbarState, SnackbarUpdate } from '.';

const DEFAULT_SNACKBAR_STATE: SnackbarState = {
	open: false,
	message: '',
	severity: 'success',
	anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
	autoHideDuration: 3000
};

/**
 * Helper hook to setup snackbar state
 * Allows you to just set a message and it will automatically use success severity and show message (this can also be passed in when required)
 * @param initialState - Initial snackbar state - can be partial so you can just provide the auto hide delay
 * @returns
 */
export const useSnackbarAlert = (initialState?: Partial<SnackbarState>): [SnackbarState, (newState: SnackbarUpdate) => void] => {
	const [snackbar, _setSnackbar] = useState<ISnackbarProps>({
		...DEFAULT_SNACKBAR_STATE,
		...(initialState ? initialState : {})
	});
	const setSnackbar = (newState: SnackbarUpdate) => {
		const update: Partial<ISnackbarProps> = typeof newState === 'string' ? { message: newState } : newState;
		_setSnackbar(prev => {
			// If we are closing we want to keep the previous state to avoid weird flashes of color/message change
			const state =
				update.open === false
					? { ...prev, open: false }
					: {
							...DEFAULT_SNACKBAR_STATE,
							open: true,
							// if we are setting an error or warning, or there is an action,
							// default to a longer auto hide duration for ease of usability
							autoHideDuration:
								update.severity === 'error' || update.severity === 'warning' || update.action
									? Math.max(prev.autoHideDuration ?? 5000, 5000)
									: prev.autoHideDuration,
							...update
						};
			return state;
		});
	};

	return [snackbar, setSnackbar];
};
